import React from 'react';
import {Box, Button, Chip, InputAdornment, TextField, Typography} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import {useColorMode} from '../../context/colorMode/colorMode.context';

/* The one line above a dashboard table (QA, 9 Oct 2026: the old drop-down
   filter bars go; the search box stays). Left: how many rows are shown and a
   chip per active header filter, with "Clear all"; right: the search box.

     <TableToolbar {...t} query={query} onQuery={setQuery} placeholder="Search by school, program…"/>

   `t` is the object `useTableControls` returns (rows · total · chips · clearAll ·
   setFilter). Leave `onQuery` out for a table without search. */
const NAVY = '#052F44';

export const TableToolbar = ({rows, total, chips = [], clearAll, setFilter, query, onQuery, placeholder = 'Search…', right, sx}) => {
    const shown = rows ? rows.length : total;
    const filtered = chips.length > 0 || (query && query.trim());
    // Dark mode: navy chip text and the light theme's dark input text and
    // border disappeared on a dark card. Light text and border there instead.
    const {isDark} = useColorMode();
    const ink = isDark ? '#FFFFFF' : NAVY;
    const darkInput = isDark ? {
        '& .MuiInputBase-input': {color: '#FFFFFF'},
        '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(255,255,255,0.23)'},
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(255,255,255,0.4)'},
    } : {};
    return (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5, ...sx}}>
            <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: 32}}>
                {typeof total === 'number' && (
                    <Typography sx={{fontSize: '12.5px', color: '#6A7282', whiteSpace: 'nowrap'}}>
                        {filtered ? `${shown} of ${total} rows` : `${total} rows`}
                    </Typography>
                )}
                {chips.map((c) => (
                    <Chip
                        key={c.key}
                        size="small"
                        label={c.label}
                        onDelete={setFilter ? () => setFilter(c.key, null) : undefined}
                        sx={{fontSize: '12px', backgroundColor: 'rgba(255, 105, 0, 0.1)', color: ink, '& .MuiChip-deleteIcon': {color: '#6A7282', fontSize: 16}}}
                    />
                ))}
                {chips.length > 1 && clearAll && (
                    <Button size="small" onClick={clearAll} sx={{textTransform: 'none', fontSize: '12px', color: ink, minWidth: 0, px: 1}}>Clear all</Button>
                )}
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0}}>
                {right}
                {onQuery && (
                    <TextField
                        size="small"
                        placeholder={placeholder}
                        value={query || ''}
                        onChange={(e) => onQuery(e.target.value)}
                        inputProps={{'aria-label': 'Search table'}}
                        sx={{minWidth: {xs: 200, sm: 280}, backgroundColor: 'var(--brand-surface, #fff)', ...darkInput}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{color: '#9CA3AF', fontSize: 20}}/>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default TableToolbar;
